import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  LayoutGrid,
  MessageSquare,
  MessagesSquare,
  Plus,
  Rocket,
  Sparkles,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  CompanyRole,
  type TCompanyResponse,
  type TCompanySection,
  type TCompanySectionThread,
} from "@/api/companies/companies.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/Button";
import { useStore } from "@/store";
import { isCompaniesEmpty } from "@/store/companies/companies.store";
import { useNavigate } from "@tanstack/react-router";

function AppDashboardSkeleton() {
  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Hero band */}
        <div className="rounded-xl border border-border bg-card p-5 shadow-sm sm:p-8">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="mt-3 h-8 w-64 sm:h-9 sm:w-80" />
          <Skeleton className="mt-5 h-12 w-full max-w-2xl rounded-xl" />
          <div className="mt-5 flex gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                className="h-8 w-24"
              />
            ))}
          </div>
        </div>

        {/* Getting Started card */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-md sm:p-6">
          <div className="mb-4 space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-3 w-64" />
          </div>
          <div className="relative space-y-6">
            <div className="absolute bottom-0 left-5 top-0 w-0.5 bg-muted-foreground/10 sm:left-6" />
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="relative flex gap-3 sm:gap-4"
              >
                <Skeleton className="relative z-10 h-10 w-10 flex-shrink-0 rounded-full sm:h-12 sm:w-12" />
                <div className="flex-1 space-y-2 pb-1 pt-1">
                  <Skeleton className="h-4 w-32 sm:h-5 sm:w-40" />
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Companies section */}
        <div className="mt-6">
          <Skeleton className="mb-3 h-6 w-36 sm:mb-4 sm:h-7 sm:w-44" />
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div className="flex gap-3">
                    <Skeleton className="h-10 w-10 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-36" />
                      <Skeleton className="h-3 w-20" />
                    </div>
                  </div>
                  <Skeleton className="h-5 w-5 rounded" />
                </div>
                <Skeleton className="h-3 w-28" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

type TTutorialStep = {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  status: "active" | "completed" | "upcoming";
  action?: () => void;
  actionLabel?: string;
};

type TRecentThread = {
  thread: TCompanySectionThread;
  company: TCompanyResponse;
  section: TCompanySection;
  activityAt: string | null;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
};

const getTodayLabel = () =>
  new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

const formatRelativeTime = (iso?: string | null) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNow(date, { addSuffix: true });
};

const getThreadTitle = (thread: TCompanySectionThread) => {
  const title = thread.title?.trim();
  if (title) return title;
  const name = thread.name?.trim();
  if (name) return name;
  return "Untitled Chat";
};

const getCompanyInitials = (name?: string | null) => {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
};

const getCompanyLastActivity = (company: TCompanyResponse) => {
  const sections = Array.isArray(company.sections) ? company.sections : [];
  const timestamps = sections
    .flatMap((section) => section.threads ?? [])
    .map((thread) => thread.updated_at ?? thread.created_at)
    .filter((value): value is string => Boolean(value));
  if (company.updated_at) {
    timestamps.push(company.updated_at);
  }
  if (timestamps.length === 0) return null;
  return timestamps.reduce((latest, current) => (current > latest ? current : latest));
};

const sortSections = (company: TCompanyResponse) => {
  const sections = Array.isArray(company.sections) ? company.sections : [];
  return [...sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

export function AppDashboard() {
  const navigate = useNavigate();
  const companies = useStore((state) => state.companies);
  const isLoadingCompanies = useStore((state) => state.isLoading);
  const companyError = useStore((state) => state.error);
  const fetchCompanies = useStore((state) => state.fetchCompanies);
  const openChatThreadSession = useStore((state) => state.openChatThreadSession);
  const closeDashboard = useStore((state) => state.closeDashboard);
  const profile = useStore((state) => state.profile);
  const firstName = profile?.first_name?.trim() ?? null;

  const [showSteps, setShowSteps] = useState(false);

  // Fetch companies on mount
  useEffect(() => {
    void fetchCompanies({ force: false });
  }, [fetchCompanies]);

  // Determine current state
  const hasCompanies = !isCompaniesEmpty(companies);
  const hasCompletedCompanies = useMemo(() => {
    if (!companies || companies.length === 0) {
      return false;
    }
    return companies.some((c) => c.onboarding_completed);
  }, [companies]);

  const incompleteCompany = useMemo(() => {
    if (!companies || companies.length === 0) {
      return null;
    }
    return companies.find((c) => !c.onboarding_completed) ?? null;
  }, [companies]);

  const stats = useMemo(() => {
    const list = companies ?? [];
    let sectionCount = 0;
    let threadCount = 0;
    for (const company of list) {
      const sections = Array.isArray(company.sections) ? company.sections : [];
      sectionCount += sections.length;
      for (const section of sections) {
        threadCount += Array.isArray(section.threads) ? section.threads.length : 0;
      }
    }
    return {
      companies: list.length,
      completed: list.filter((c) => c.onboarding_completed).length,
      sections: sectionCount,
      threads: threadCount,
    };
  }, [companies]);

  const recentThreads = useMemo<TRecentThread[]>(() => {
    const list = companies ?? [];
    const entries: TRecentThread[] = [];
    for (const company of list) {
      if (!company.onboarding_completed) continue;
      const sections = Array.isArray(company.sections) ? company.sections : [];
      for (const section of sections) {
        for (const thread of section.threads ?? []) {
          entries.push({
            thread,
            company,
            section,
            activityAt: thread.updated_at ?? thread.created_at ?? null,
          });
        }
      }
    }
    return entries.sort((a, b) => (b.activityAt ?? "").localeCompare(a.activityAt ?? "")).slice(0, 6);
  }, [companies]);

  const handleStartOnboarding = () => {
    if (incompleteCompany) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: incompleteCompany.id },
      });
    } else {
      // Navigate to onboarding start page (will create company during onboarding)
      void navigate({
        to: "/app/onboarding",
      });
    }
  };

  const handleAddCompany = () => {
    void navigate({ to: "/app/onboarding", search: { new: true } });
  };

  const openCompany = (company: TCompanyResponse) => {
    if (!company.onboarding_completed) {
      void navigate({
        to: "/app/companies/$company_id/onboarding",
        params: { company_id: company.id },
      });
      return;
    }
    const sortedSections = sortSections(company);
    if (sortedSections.length > 0) {
      void navigate({
        to: "/app/companies/$company_id/sections/$section_id/dashboard",
        params: {
          company_id: company.id,
          section_id: sortedSections[0].id ?? "",
        },
      });
    }
  };

  const openSection = (company: TCompanyResponse, section: TCompanySection) => {
    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/dashboard",
      params: { company_id: company.id, section_id: section.id },
    });
  };

  const openNewChat = (company: TCompanyResponse, section?: TCompanySection) => {
    const target = section ?? sortSections(company)[0];
    if (!target) return;
    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/new",
      params: { company_id: company.id, section_id: target.id },
    });
  };

  const openThread = ({ thread, company, section }: TRecentThread) => {
    // Investor conversations use a dedicated route that needs founder context,
    // so land on the section dashboard instead of deep-linking the thread.
    if (company.role === CompanyRole.INVESTOR) {
      openSection(company, section);
      return;
    }
    closeDashboard();
    openChatThreadSession({
      threadId: thread.id,
      companyId: company.id,
      companyName: company.name,
      sectionId: section.id,
      sectionName: section.name ?? company.name ?? "Thread",
      title: thread.title ?? "Chat Thread",
      initialPrompt: null,
    });
    void navigate({
      to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
      params: {
        company_id: company.id,
        section_id: section.id,
        thread_id: thread.id,
      },
    });
  };

  // Define tutorial steps
  const steps: TTutorialStep[] = [
    {
      id: 1,
      title: "Add Your Company",
      description: "Tell Fundy what you're building. This creates your company workspace.",
      icon: <Building2 className="h-4 w-4" />,
      status: hasCompanies ? "completed" : "active",
      action: handleStartOnboarding,
      actionLabel: "Add Company",
    },
    {
      id: 2,
      title: "Complete Onboarding",
      description: "Answer a few questions so Fundy understands your business and your stage.",
      icon: <Rocket className="h-4 w-4" />,
      status: hasCompletedCompanies ? "completed" : hasCompanies ? "active" : "upcoming",
      action: hasCompanies && !hasCompletedCompanies ? handleStartOnboarding : undefined,
      actionLabel: hasCompanies && !hasCompletedCompanies ? "Continue Onboarding" : undefined,
    },
    {
      id: 3,
      title: "Start Using Fundy",
      description: "Open a section dashboard or ask Fundy your first question.",
      icon: <Sparkles className="h-4 w-4" />,
      status: hasCompletedCompanies ? "completed" : "upcoming",
    },
  ];

  const completedSteps = steps.filter((step) => step.status === "completed").length;

  if (isLoadingCompanies) {
    return <AppDashboardSkeleton />;
  }

  if (companyError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3 rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          <p>{companyError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void fetchCompanies({ force: true })}
            className="flex items-center gap-2"
          >
            Try again
          </Button>
        </div>
      </div>
    );
  }

  const showFullSteps = !hasCompletedCompanies || showSteps;
  const lastActivityLabel = formatRelativeTime(recentThreads[0]?.activityAt);

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Hero band */}
        <section className="relative overflow-hidden rounded-xl border border-border bg-card shadow-sm motion-safe:animate-fade-up">
          {/* Quiet primary wash, strongest at the top-left where the greeting sits */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent"
          />
          <div className="relative p-5 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {getTodayLabel()}
                </p>
                <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
                  {firstName ? `${getGreeting()}, ${firstName}` : getGreeting()}
                </h1>
                {!hasCompanies && (
                  <p className="mt-2 max-w-md text-sm text-muted-foreground">
                    Welcome to Fundy. Set up your first company below and start getting fundraising insights.
                  </p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                {incompleteCompany && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStartOnboarding}
                  >
                    <Rocket className="mr-1.5 h-4 w-4" />
                    Resume Onboarding
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={hasCompanies ? handleAddCompany : handleStartOnboarding}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  {hasCompanies ? "New Company" : "Add Company"}
                </Button>
              </div>
            </div>

            {/* Inline stat strip */}
            {hasCompanies && (
              <dl className="mt-6 flex flex-wrap items-center gap-x-0 gap-y-3">
                {[
                  { label: stats.companies === 1 ? "Company" : "Companies", value: stats.companies },
                  { label: stats.sections === 1 ? "Section" : "Sections", value: stats.sections },
                  { label: stats.threads === 1 ? "Conversation" : "Conversations", value: stats.threads },
                  ...(stats.completed < stats.companies
                    ? [{ label: "Onboarded", value: `${stats.completed}/${stats.companies}` }]
                    : []),
                ].map((stat, index) => (
                  <div
                    key={stat.label}
                    className={`flex items-baseline gap-2 pr-5 sm:pr-6 ${
                      index > 0 ? "border-l border-border pl-5 sm:pl-6" : ""
                    }`}
                  >
                    <dd className="text-lg font-semibold tabular-nums text-foreground sm:text-xl">{stat.value}</dd>
                    <dt className="text-xs text-muted-foreground sm:text-sm">{stat.label}</dt>
                  </div>
                ))}
                {lastActivityLabel && (
                  <div className="flex items-center gap-1.5 border-l border-border pl-5 text-xs text-muted-foreground sm:pl-6">
                    <Clock className="h-3.5 w-3.5" />
                    Last activity {lastActivityLabel}
                  </div>
                )}
              </dl>
            )}
          </div>
        </section>

        {/* Getting Started — collapses once the user has a fully onboarded company */}
        {showFullSteps ? (
          <Card
            className="bg-card shadow-md motion-safe:animate-fade-up"
            style={{ animationDelay: "80ms" }}
            data-tour="dashboard-getting-started"
          >
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-lg font-semibold text-foreground sm:text-xl">Getting Started</CardTitle>
                  <CardDescription className="text-sm text-muted-foreground">
                    Set up Fundy in three steps.
                  </CardDescription>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <span className="text-xs tabular-nums text-muted-foreground">{completedSteps} of 3 done</span>
                  {hasCompletedCompanies && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSteps(false)}
                      className="text-muted-foreground"
                    >
                      <ChevronUp className="mr-1 h-4 w-4" />
                      Hide
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-5 sm:px-6 sm:pb-6">
              <div>
                {steps.map((step, index) => {
                  const isLast = index === steps.length - 1;
                  const isActive = step.status === "active";
                  const isCompleted = step.status === "completed";

                  return (
                    <div
                      key={step.id}
                      className="flex gap-3 sm:gap-4"
                    >
                      {/* Progress rail: node + connecting segment */}
                      <div className="flex w-9 flex-shrink-0 flex-col items-center">
                        {isCompleted ? (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground">
                            <Check
                              className="h-4 w-4"
                              strokeWidth={3}
                            />
                          </div>
                        ) : isActive ? (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-primary bg-primary/10 text-primary ring-4 ring-primary/15">
                            {step.icon}
                          </div>
                        ) : (
                          <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-border bg-card text-xs font-semibold text-muted-foreground/60">
                            {step.id}
                          </div>
                        )}
                        {!isLast && (
                          <div
                            className={`my-1 w-0.5 flex-1 rounded-full ${isCompleted ? "bg-primary" : "bg-border"}`}
                          />
                        )}
                      </div>

                      {/* Step content: the active step gets the space */}
                      <div className={`min-w-0 flex-1 ${isLast ? "" : "pb-5"} ${isActive ? "" : "pt-0.5"}`}>
                        {isActive && (
                          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-primary">
                            Step {step.id} of {steps.length}
                          </p>
                        )}
                        <p
                          className={
                            isActive
                              ? "mt-0.5 text-base font-semibold text-foreground sm:text-lg"
                              : isCompleted
                                ? "text-sm font-medium text-muted-foreground"
                                : "text-sm font-medium text-muted-foreground/60"
                          }
                        >
                          {step.title}
                        </p>
                        {isActive && (
                          <>
                            <p className="mt-1 max-w-md text-sm text-muted-foreground">{step.description}</p>
                            {step.action && step.actionLabel && (
                              <div
                                className="mt-3"
                                data-tour="dashboard-onboarding-cta"
                              >
                                <Button
                                  onClick={step.action}
                                  size="sm"
                                >
                                  {step.actionLabel}
                                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ) : (
          <button
            type="button"
            onClick={() => setShowSteps(true)}
            className="flex w-full items-center justify-between rounded-lg border border-border bg-card px-4 py-3 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 motion-safe:animate-fade-up"
            style={{ animationDelay: "80ms" }}
            data-tour="dashboard-getting-started"
          >
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <div>
                <span className="text-sm font-semibold text-foreground">Setup complete</span>
                <span className="ml-2 hidden text-xs text-muted-foreground sm:inline">
                  You're ready to use Fundy — open a company below or start a new chat.
                </span>
              </div>
            </div>
            <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              View steps
              <ChevronDown className="h-4 w-4" />
            </span>
          </button>
        )}

        {/* Companies + Recent conversations */}
        {hasCompanies && (
          <div
            className="grid gap-6 motion-safe:animate-fade-up lg:grid-cols-3"
            style={{ animationDelay: "160ms" }}
          >
            {/* Companies List */}
            <div className={`min-w-0 ${recentThreads.length > 0 ? "lg:col-span-2" : "lg:col-span-3"}`}>
              <h2 className="mb-3 text-lg font-semibold text-foreground sm:mb-4 sm:text-xl">
                {companies?.length === 1 ? "Your Company" : "Your Companies"}
              </h2>
              <div
                className={`grid gap-3 sm:gap-4 ${
                  recentThreads.length > 0 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"
                }`}
              >
                {companies?.map((company) => {
                  const sections = Array.isArray(company.sections) ? company.sections : [];
                  const sortedSections = sortSections(company);
                  const threadCount = sections.reduce(
                    (sum, section) => sum + (Array.isArray(section.threads) ? section.threads.length : 0),
                    0
                  );
                  const isCompleted = company.onboarding_completed;
                  const lastActivity = formatRelativeTime(getCompanyLastActivity(company));
                  const isInvestor = company.role === CompanyRole.INVESTOR;

                  return (
                    <Card
                      key={company.id}
                      className={`group cursor-pointer transition-all hover:border-primary/40 hover:shadow-md ${
                        isCompleted ? "border-border bg-card shadow-sm" : "border-primary/30 bg-primary/5"
                      }`}
                      onClick={() => openCompany(company)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex min-w-0 flex-1 items-start gap-3">
                            {/* Monogram */}
                            <div
                              aria-hidden
                              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-sm font-semibold text-primary"
                            >
                              {getCompanyInitials(company.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <CardTitle className="line-clamp-2 text-base font-semibold text-foreground sm:text-lg">
                                {company.name ?? company.id}
                              </CardTitle>
                              <p className="mt-0.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                                {isInvestor ? "Investor" : "Fundraiser"}
                              </p>
                            </div>
                          </div>
                          {!isCompleted && (
                            <Badge
                              variant="outline"
                              className="flex-shrink-0 border-primary/40 px-2 py-0 text-[10px] uppercase tracking-wide text-primary"
                            >
                              Onboarding
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {isCompleted ? (
                          <>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <LayoutGrid className="h-3.5 w-3.5" />
                                {sections.length} section{sections.length !== 1 ? "s" : ""}
                              </span>
                              <span className="inline-flex items-center gap-1">
                                <MessagesSquare className="h-3.5 w-3.5" />
                                {threadCount} chat{threadCount !== 1 ? "s" : ""}
                              </span>
                              {lastActivity && (
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {lastActivity}
                                </span>
                              )}
                            </div>

                            {/* Section shortcuts */}
                            {sortedSections.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-1.5">
                                {sortedSections.slice(0, 3).map((section) => (
                                  <button
                                    key={section.id}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openSection(company, section);
                                    }}
                                    className="rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                                  >
                                    {section.name}
                                  </button>
                                ))}
                                {sortedSections.length > 3 && (
                                  <span className="px-1 py-0.5 text-xs text-muted-foreground/70">
                                    +{sortedSections.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}

                            <div className="mt-3 flex items-center justify-between">
                              <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-muted-foreground transition-colors group-hover:text-primary sm:text-sm">
                                Open dashboard
                                <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
                              </span>
                              {!isInvestor && sortedSections.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNewChat(company);
                                  }}
                                >
                                  <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                                  New Chat
                                </Button>
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-xs text-muted-foreground">
                              Finish setup to unlock sections and chats.
                            </span>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                void navigate({
                                  to: "/app/companies/$company_id/onboarding",
                                  params: { company_id: company.id },
                                });
                              }}
                            >
                              Continue
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Recent Conversations */}
            {recentThreads.length > 0 && (
              <div className="min-w-0">
                <h2 className="mb-3 text-lg font-semibold text-foreground sm:mb-4 sm:text-xl">Recent Conversations</h2>
                <Card className="bg-card shadow-sm">
                  <CardContent className="p-2">
                    <ul className="divide-y divide-border/60">
                      {recentThreads.map((entry) => (
                        <li key={entry.thread.id}>
                          <button
                            type="button"
                            onClick={() => openThread(entry)}
                            className="group flex w-full items-start gap-2.5 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                          >
                            <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground group-hover:text-primary" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-foreground">
                                {getThreadTitle(entry.thread)}
                              </span>
                              <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                                {entry.company.name} · {entry.section.name}
                                {entry.activityAt ? ` · ${formatRelativeTime(entry.activityAt)}` : ""}
                              </span>
                            </span>
                            <ArrowRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground/0 transition-all group-hover:text-primary" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
